import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./VoiceLiveDemo.css";
import { IPolicy } from "../../models/user/dashboard/Dashboard";
import {
  INITIAL_FIELD_GROUPS,
  IntakeState,
  TranscriptTurn,
  agentClosedConversation,
  applyHintsFromFullTranscript,
  arrayBufferToBase64,
  base64ToInt16Array,
  bootstrapClaimStateFromTranscript,
  buildEmptyState,
  claimantAskedToClose,
  getVoiceApiOrigin,
  getVoiceWsOrigin,
  getNextMissingFieldCoach,
  mergeClaimFields,
  mergeLiveTranscript,
  normalizeServerState,
  transcriptHasClaimantAnswer,
  nowTime,
  resampleToPcm16,
  setClaimFieldValue,
  upsertStreamingTurn,
} from "./voiceLiveDemoLogic";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import { GetPoliciesData } from "../Dashboard/DashboardAction";
import { getCookie } from "../../utils/Utility";
import {
  buildPolicyNumberPrompt,
  displayPolicyTypeLabel,
  extractPolicyNumberFromText,
  filterPoliciesByVoiceType,
  getAccountPolicyTypeLabels,
  getPoliciesRequestParams,
  matchPolicyBySpokenHint,
  policyCandidatesForVoiceType,
  readPolicyListFromResponse,
  resolvePoliciesForVoiceType,
} from "./voicePoliciesApi";
import { detectVoiceFormIntentFromTranscript } from "./voiceIntentDetection";
import { VoiceFormIntentId } from "./voiceFormRegistry";
import {
  buildIntentSwitchClientFields,
  isInternalTranscriptLeak,
  VOICE_MENU_GREETING,
} from "./voiceFormPrompts";
import { useVoiceDynamicForm } from "./useVoiceDynamicForm";
import VoiceDynamicFormPanel from "./VoiceDynamicFormPanel";
import { useCsrAssist } from "./useCsrAssist";
import CsrAssistEnquiresPanel from "./CsrAssistEnquiresPanel";
import { useVoiceSentiment } from "./useVoiceSentiment";
import SentimentIndicator from "./SentimentIndicator";

/** REST/WebSocket targets Java {@code /voiceForms/api/*} and {@code /voiceForms/ws/live}. */
const VoiceLiveDemo: React.FC = () => {
  const apiOrigin = useMemo(() => getVoiceApiOrigin(), []);
  const wsOrigin = useMemo(() => getVoiceWsOrigin(), []);
  // Legacy Python REST/WS base (REACT_APP_CHAT_GEMINI_LIVE_URL, e.g. http://127.0.0.1:8002):
  // const apiOrigin = (process.env.REACT_APP_CHAT_GEMINI_LIVE_URL || "http://127.0.0.1:8002").replace(/\/$/, "");
  // const wsOrigin = apiOrigin.replace(/^http/, "ws");
  const dispatch = useAppDispatch();
  const dashboardPolicies = useAppSelector(
    (state: RootState) => state.dashboard.policiesData?.policyList
  );
  const userDetails = useAppSelector((state: RootState) => state.userAuth.userDetails);

  const [state, setState] = useState<IntakeState>(() =>
    buildEmptyState(apiOrigin)
  );
  const [callStatus, setCallStatus] = useState("Connecting to backend");
  const [isRecording, setIsRecording] = useState(false);
  const [textInput, setTextInput] = useState("");
  const [textDisabled, setTextDisabled] = useState(true);
  const sessionIdRef = useRef<string | null>(null);
  const liveSocketRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const inputProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const inputSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const nextPlaybackTimeRef = useRef(0);
  const activePlaybackSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const closeAfterAgentTurnRef = useRef(false);
  const mountedRef = useRef(true);
  const isRecordingRef = useRef(false);
  const selectedPolicyIdRef = useRef<string | null>(null);
  const pendingPolicyCandidatesRef = useRef<IPolicy[]>([]);
  const customerPoliciesRef = useRef<IPolicy[]>([]);
  const policiesLoadKeyRef = useRef<string | null>(null);
  const stateRef = useRef(state);
  const lastClientFieldsKeyRef = useRef("");
  const lastAgentPromptHintRef = useRef("");
  const notifyFieldsTimerRef = useRef<number | null>(null);
  const pendingClientFieldsRef = useRef<Record<string, string> | null>(null);
  const activeVoiceFormIdRef = useRef<VoiceFormIntentId | null>(null);
  /** True while agent audio is playing — mic is muted (half-duplex) to avoid VAD delay. */
  const agentSpeakingRef = useRef(false);
  const userSpeechActiveRef = useRef(false);
  const userSilenceMsRef = useRef(0);
  const speechEndSentRef = useRef(false);

  const [customerPolicies, setCustomerPolicies] = useState<IPolicy[]>([]);
  const [policiesLoading, setPoliciesLoading] = useState(true);
  const [policiesError, setPoliciesError] = useState<string | null>(null);
  const [activeVoiceFormId, setActiveVoiceFormId] =
    useState<VoiceFormIntentId | null>(null);
  const isMenuMode = activeVoiceFormId === null;
  const isClaimForm = activeVoiceFormId === "file_claim";

  const dynamicForm = useVoiceDynamicForm(
    isClaimForm || isMenuMode ? null : activeVoiceFormId,
    state.transcript,
    customerPolicies
  );
  const csrAssist = useCsrAssist({
    transcript: state.transcript,
    activeFormId: activeVoiceFormId,
    claimFields: state.fields,
    dynamicFormSchema: dynamicForm.formSchema,
    dynamicFieldStatuses: dynamicForm.fieldStatuses,
  });
  const voiceSentiment = useVoiceSentiment(state.transcript);

  const formSectionTitle = isMenuMode
    ? "Form"
    : isClaimForm
      ? "Claim intake"
      : dynamicForm.definition?.title ?? "Form";

  const formSectionStatus = (() => {
    if (isMenuMode) return "Listening";
    if (!isClaimForm) {
      if (dynamicForm.formLoading) return "Loading form…";
      if (policiesLoading) return "Loading policies…";
      if (policiesError) return policiesError;
      return "Live";
    }
    if (policiesLoading) return "Loading policies…";
    if (policiesError) return policiesError;
    return "Auto-saving";
  })();

  const appendLocalError = useCallback((message: string) => {
    const short =
      /429|RESOURCE_EXHAUSTED|quota/i.test(message)
        ? "Gemini quota reached — form is updating from your spoken answers."
        : message.length > 160
          ? `${message.slice(0, 157)}...`
          : message;
    setCallStatus(short);
    setState((prev) => ({
      ...prev,
      events: [
        ...prev.events,
        {
          tone: "danger",
          title: "Backend notice",
          detail: short,
          rule: "API-ERR",
          time: nowTime(),
        },
      ],
    }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPoliciesError(null);

    const hydrate = (list: IPolicy[]) => {
      customerPoliciesRef.current = list;
      setCustomerPolicies(list);
      setPoliciesLoading(false);
    };

    const params = getPoliciesRequestParams({
      customerId: userDetails?.customerId,
      carrier: getCookie("carrier") || undefined,
      system: getCookie("system") || undefined,
    });

    if (!params) {
      policiesLoadKeyRef.current = null;
      setPoliciesLoading(false);
      setPoliciesError("Sign in to load your policies.");
      return () => {
        cancelled = true;
      };
    }

    const loadKey = `${params.customerId}|${params.carrier}|${params.system}`;

    if (dashboardPolicies?.length) {
      hydrate([...dashboardPolicies]);
      policiesLoadKeyRef.current = loadKey;
      return () => {
        cancelled = true;
      };
    }

    if (policiesLoadKeyRef.current === loadKey) {
      return () => {
        cancelled = true;
      };
    }

    policiesLoadKeyRef.current = loadKey;
    setPoliciesLoading(true);

    dispatch(GetPoliciesData(params))
      .unwrap()
      .then((response) => {
        if (cancelled) return;
        if (response.status !== "SUCCESS") {
          setPoliciesError(response.message || "Could not load your policies.");
          setPoliciesLoading(false);
          return;
        }
        const list = readPolicyListFromResponse(response);
        hydrate(list);
      })
      .catch(() => {
        if (cancelled) return;
        policiesLoadKeyRef.current = null;
        if (!customerPoliciesRef.current.length) {
          setPoliciesError("Could not load your policies.");
          setCustomerPolicies([]);
        }
        setPoliciesLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Intentionally omit dashboardPolicies: including it re-ran this effect on every
    // GetPoliciesData fulfill and caused an infinite policies API loop.
  }, [dispatch, userDetails?.customerId]);

  useEffect(() => {
    if (!dashboardPolicies?.length || customerPoliciesRef.current.length) {
      return;
    }
    customerPoliciesRef.current = [...dashboardPolicies];
    setCustomerPolicies([...dashboardPolicies]);
    setPoliciesLoading(false);
  }, [dashboardPolicies]);

  const stopAgentPlayback = useCallback(() => {
    activePlaybackSourcesRef.current.forEach((source) => {
      try {
        source.stop();
      } catch {
        /* already stopped */
      }
    });
    activePlaybackSourcesRef.current = [];
    agentSpeakingRef.current = false;
    if (audioContextRef.current) {
      nextPlaybackTimeRef.current = audioContextRef.current.currentTime;
    }
  }, []);

  const hasClaimantSpoken = useCallback(() => {
    return stateRef.current.transcript.some(
      (t) => t.speaker === "Claimant" && (t.text?.trim().length ?? 0) >= 5
    );
  }, []);

  const buildMenuAgentFields = useCallback((): Record<string, string> => {
    const policies = customerPoliciesRef.current;
    const accountTypes = getAccountPolicyTypeLabels(policies);
    const payload: Record<string, string> = {
      formIntent: "menu",
      formTitle: "How can I help",
      agentPromptHint: VOICE_MENU_GREETING,
    };
    if (policiesLoading) {
      payload.policiesStatus = "loading";
    }
    if (accountTypes.length) {
      payload.accountPolicyTypes = accountTypes.join(", ");
    }
    return payload;
  }, [policiesLoading]);

  const buildFileClaimClientFields = useCallback(
    (s: IntakeState): Record<string, string> => {
      const payload: Record<string, string> = {
        formIntent: "file_claim",
        formTitle: "File a claim",
      };
      if (policiesLoading) {
        payload.policiesStatus = "loading";
      }
      const policies = customerPoliciesRef.current;
      const accountTypes = getAccountPolicyTypeLabels(policies);
      if (accountTypes.length) {
        payload.accountPolicyTypes = accountTypes.join(", ");
      }
      if (!transcriptHasClaimantAnswer(s.transcript)) {
        return payload;
      }
      if (policiesLoading) {
        return payload;
      }
      const policyType = s.fields.policyType?.value?.trim();
      if (policyType && s.fields.policyType?.status !== "missing") {
        payload.policyType = policyType;
        const matches = filterPoliciesByVoiceType(policies, policyType);
        payload.policyMatchCount = String(matches.length);
        const confirmedNo = s.fields.policyNumber?.value?.trim();
        const confirmed =
          confirmedNo &&
          s.fields.policyNumber?.status === "complete" &&
          !/^missing:/i.test(confirmedNo) &&
          matches.some((p) => p.carrierPolicyNo === confirmedNo);

        if (matches.length === 1) {
          payload.policyNumber = matches[0].carrierPolicyNo;
        } else if (matches.length > 1) {
          const nums = matches.map((p) => p.carrierPolicyNo);
          payload.policyOptions = nums.join(", ");
          if (confirmed) {
            payload.policyNumber = confirmedNo;
            payload.policyMatchCount = "1";
          } else {
            const ask = buildPolicyNumberPrompt(
              displayPolicyTypeLabel(policyType),
              nums
            );
            if (ask) payload.agentPromptHint = ask;
          }
        } else if (confirmed) {
          payload.policyNumber = confirmedNo;
        }
      }

      if (!payload.agentPromptHint) {
        const pendingPolicyCount = Number(payload.policyMatchCount || "0") || 0;
        const policyOptionLabels = payload.policyOptions || "";
        const next = getNextMissingFieldCoach(
          s.fields,
          pendingPolicyCount,
          policyOptionLabels
        );
        if (next) payload.agentPromptHint = next;
      }

      // Sync already-filled claim fields so the live agent can skip them
      // (e.g. Highway from "on the highway" in the caller's story).
      for (const id of [
        "locationType",
        "incidentType",
        "date",
        "time",
        "incidentLocation",
        "incidentDescription",
      ] as const) {
        const field = s.fields[id];
        const value = field?.value?.trim();
        if (
          field?.status === "complete" &&
          value &&
          !/^missing:/i.test(value)
        ) {
          payload[id] = value;
        }
      }
      return payload;
    },
    [policiesLoading]
  );

  const buildAgentContextFields = useCallback((): Record<string, string> => {
    if (activeVoiceFormIdRef.current === null) {
      return buildMenuAgentFields();
    }
    if (activeVoiceFormIdRef.current !== "file_claim") {
      return {};
    }
    return buildFileClaimClientFields(stateRef.current);
  }, [buildMenuAgentFields, buildFileClaimClientFields]);

  const notifyAgentKnownFields = useCallback(
    (fields?: Record<string, string>) => {
      const payload = fields || buildAgentContextFields();
      if (!Object.keys(payload).length) return;
      if (liveSocketRef.current?.readyState !== WebSocket.OPEN) return;
      // Debounce: rapid policy/intent syncs were flooding the live socket and
      // interrupting Gemini replies (felt like long "Speaking…" delays).
      pendingClientFieldsRef.current = payload;
      if (notifyFieldsTimerRef.current != null) {
        window.clearTimeout(notifyFieldsTimerRef.current);
      }
      notifyFieldsTimerRef.current = window.setTimeout(() => {
        notifyFieldsTimerRef.current = null;
        const toSend = pendingClientFieldsRef.current;
        pendingClientFieldsRef.current = null;
        if (!toSend || liveSocketRef.current?.readyState !== WebSocket.OPEN) {
          return;
        }
        const key = JSON.stringify(toSend);
        if (key === lastClientFieldsKeyRef.current) return;
        lastClientFieldsKeyRef.current = key;
        try {
          liveSocketRef.current.send(
            JSON.stringify({ type: "client_fields", fields: toSend })
          );
        } catch {
          /* ignore */
        }
      }, 250);
    },
    [buildAgentContextFields]
  );

  const applyPolicyLookup = useCallback(
    (
      prev: IntakeState,
      policyTypeValue: string,
      spokenHint = ""
    ): IntakeState => {
      const policies = customerPoliciesRef.current;
      if (!policyTypeValue.trim()) return prev;

      const displayType = displayPolicyTypeLabel(policyTypeValue);
      let next = setClaimFieldValue(
        prev,
        "policyType",
        displayType,
        prev.fields.policyType?.status === "complete" ? "complete" : "pending",
        "customer policies"
      );

      if (!policies.length) {
        if (policiesLoading) {
          return setClaimFieldValue(
            next,
            "policyNumber",
            "Looking up your policy…",
            "pending",
            "customer policies"
          );
        }
        return setClaimFieldValue(
          next,
          "policyNumber",
          policiesError || "No policies on your account",
          "missing",
          "customer policies"
        );
      }

      const result = resolvePoliciesForVoiceType(policies, policyTypeValue);

      if (result.kind === "single") {
        const { policy } = result;
        selectedPolicyIdRef.current = policy.policyId;
        pendingPolicyCandidatesRef.current = [];
        notifyAgentKnownFields({
          policyType: displayType,
          policyNumber: policy.carrierPolicyNo,
        });
        return setClaimFieldValue(
          next,
          "policyNumber",
          policy.carrierPolicyNo,
          "complete"
        );
      }

      if (result.kind === "multiple") {
        const candidates = filterPoliciesByVoiceType(
          policies,
          policyTypeValue
        );
        const hintText =
          spokenHint.trim() ||
          extractPolicyNumberFromText(
            prev.transcript
              .filter((t) => t.speaker === "Claimant" && t.text?.trim())
              .map((t) => t.text)
              .join(" ")
          ) ||
          "";
        if (hintText) {
          const spokenMatch = matchPolicyBySpokenHint(candidates, hintText);
          if (spokenMatch.kind === "single") {
            const { policy } = spokenMatch;
            selectedPolicyIdRef.current = policy.policyId;
            pendingPolicyCandidatesRef.current = [];
            notifyAgentKnownFields({
              policyType: displayType,
              policyNumber: policy.carrierPolicyNo,
            });
            return setClaimFieldValue(
              next,
              "policyNumber",
              policy.carrierPolicyNo,
              "complete"
            );
          }
        }
        pendingPolicyCandidatesRef.current = candidates;
        selectedPolicyIdRef.current = null;
        const policyNums = candidates.map((p) => p.carrierPolicyNo);
        window.setTimeout(() => {
          notifyAgentKnownFields({
            policyType: displayType,
            policyMatchCount: String(policyNums.length),
            policyOptions: policyNums.join(", "),
            accountPolicyTypes: getAccountPolicyTypeLabels(policies).join(", "),
            agentPromptHint: buildPolicyNumberPrompt(displayType, policyNums),
          });
        }, 0);
        return {
          ...next,
          fields: {
            ...next.fields,
            policyNumber: {
              label: "Policy number",
              value: "Missing: policy number",
              status: "missing",
              source: "customer policies",
            },
          },
        };
      }

      if (result.kind === "none") {
        pendingPolicyCandidatesRef.current = [];
        if (!policies.length && policiesLoading) {
          return setClaimFieldValue(
            next,
            "policyNumber",
            "Looking up your policy…",
            "pending",
            "customer policies"
          );
        }
        const message = !policies.length
          ? policiesError || "No policies on your account"
          : `No ${displayType} policy on your account`;
        return setClaimFieldValue(
          next,
          "policyNumber",
          message,
          "missing",
          "customer policies"
        );
      }

      return next;
    },
    [notifyAgentKnownFields, policiesError, policiesLoading]
  );

  const applyPolicyNumberFromSpeech = useCallback(
    (prev: IntakeState, spoken: string): IntakeState => {
      if (!spoken.trim() || prev.fields.policyNumber?.status === "complete") {
        return prev;
      }
      const policies = customerPoliciesRef.current;
      const voiceType = prev.fields.policyType?.value || "";
      let candidates = pendingPolicyCandidatesRef.current;
      if (!candidates.length && voiceType) {
        candidates = policyCandidatesForVoiceType(policies, voiceType);
      }
      if (!candidates.length) return prev;

      const hint = extractPolicyNumberFromText(spoken) || spoken.trim();
      const match = matchPolicyBySpokenHint(candidates, hint);
      if (match.kind !== "single") return prev;

      const { policy } = match;
      selectedPolicyIdRef.current = policy.policyId;
      pendingPolicyCandidatesRef.current = [];
      const displayType =
        prev.fields.policyType?.value || displayPolicyTypeLabel("Auto");
      notifyAgentKnownFields({
        policyType: displayType,
        policyNumber: policy.carrierPolicyNo,
      });
      return setClaimFieldValue(
        prev,
        "policyNumber",
        policy.carrierPolicyNo,
        "complete"
      );
    },
    [notifyAgentKnownFields]
  );

  const voicePolicyType = useMemo(() => {
    const field = state.fields.policyType;
    if (!field || field.status === "missing") return "";
    return field.value.trim();
  }, [state.fields.policyType]);

  const syncPolicyNumberFromAccount = useCallback(() => {
    const s = stateRef.current;
    if (!hasClaimantSpoken()) return;

    const policyType = s.fields.policyType?.value?.trim();
    if (!policyType || s.fields.policyType?.status === "missing") return;

    const claimantText = s.transcript
      .filter((t) => t.speaker === "Claimant" && t.text?.trim())
      .map((t) => t.text)
      .join(" ");

    setState((prev) => {
      const displayType = displayPolicyTypeLabel(policyType);
      const matches = filterPoliciesByVoiceType(
        customerPoliciesRef.current,
        policyType
      );
      const currentNo = prev.fields.policyNumber?.value?.trim();
      const numberMatchesType =
        prev.fields.policyNumber?.status === "complete" &&
        !!currentNo &&
        !/^missing:/i.test(currentNo) &&
        matches.some((p) => p.carrierPolicyNo === currentNo);
      if (
        numberMatchesType &&
        prev.fields.policyType?.value?.trim() === displayType
      ) {
        return prev;
      }
      return applyPolicyLookup(prev, policyType, claimantText);
    });
  }, [applyPolicyLookup, hasClaimantSpoken]);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    activeVoiceFormIdRef.current = activeVoiceFormId;
  }, [activeVoiceFormId]);

  const applyDetectedFormIntent = useCallback(
    (hit: ReturnType<typeof detectVoiceFormIntentFromTranscript>) => {
      if (!hit || hit.formId === activeVoiceFormIdRef.current) return;
      const previousForm = activeVoiceFormIdRef.current;
      activeVoiceFormIdRef.current = hit.formId;
      setActiveVoiceFormId(hit.formId);
      lastClientFieldsKeyRef.current = "";
      lastAgentPromptHintRef.current = "";
      if (previousForm === "file_claim" && hit.formId !== "file_claim") {
        selectedPolicyIdRef.current = null;
        pendingPolicyCandidatesRef.current = [];
      }
      if (hit.formId === "file_claim" && previousForm !== "file_claim") {
        const accountTypes = getAccountPolicyTypeLabels(
          customerPoliciesRef.current
        );
        let bootstrapped = bootstrapClaimStateFromTranscript(
          apiOrigin,
          stateRef.current.transcript,
          accountTypes
        );
        const claimantText = stateRef.current.transcript
          .filter((t) => t.speaker === "Claimant" && t.text?.trim())
          .map((t) => t.text)
          .join(" ");
        const policyType = bootstrapped.fields.policyType?.value?.trim();
        if (
          policyType &&
          bootstrapped.fields.policyType?.status !== "missing" &&
          customerPoliciesRef.current.length > 0
        ) {
          bootstrapped = applyPolicyLookup(
            bootstrapped,
            policyType,
            claimantText
          );
        }
        stateRef.current = {
          ...stateRef.current,
          route: bootstrapped.route,
          progress: bootstrapped.progress,
          fields: bootstrapped.fields,
        };
        setState((prev) => ({
          ...prev,
          route: bootstrapped.route,
          progress: bootstrapped.progress,
          fields: bootstrapped.fields,
        }));
        if (liveSocketRef.current?.readyState === WebSocket.OPEN) {
          lastClientFieldsKeyRef.current = "";
          notifyAgentKnownFields(buildFileClaimClientFields(bootstrapped));
        }
        return;
      }
      if (liveSocketRef.current?.readyState === WebSocket.OPEN) {
        const accountTypes = getAccountPolicyTypeLabels(
          customerPoliciesRef.current
        );
        notifyAgentKnownFields(
          buildIntentSwitchClientFields(hit.formId, {
            policiesLoading,
            accountPolicyTypes: accountTypes.join(", "),
          })
        );
      }
    },
    [apiOrigin, policiesLoading, notifyAgentKnownFields, buildFileClaimClientFields, applyPolicyLookup]
  );

  const claimantIntentKey = state.transcript
    .filter((t) => t.speaker === "Claimant" && t.text?.trim())
    .map((t) => `${t.streaming ? "s" : "f"}:${t.text}`)
    .join("|");

  useEffect(() => {
    const hit = detectVoiceFormIntentFromTranscript(
      state.transcript,
      activeVoiceFormIdRef.current
    );
    if (hit) applyDetectedFormIntent(hit);
  }, [claimantIntentKey, applyDetectedFormIntent, state.transcript]);

  useEffect(() => {
    if (isMenuMode || isClaimForm) return;
    if (dynamicForm.formLoading || !dynamicForm.formSchema) return;
    if (liveSocketRef.current?.readyState !== WebSocket.OPEN) return;
    const payload = dynamicForm.buildClientFieldsPayload();
    if (policiesLoading) {
      payload.policiesStatus = "loading";
    }
    const hint = payload.agentPromptHint || "";
    if (!hint) return;
    if (hint !== lastAgentPromptHintRef.current) {
      lastAgentPromptHintRef.current = hint;
      lastClientFieldsKeyRef.current = "";
    }
    if (Object.keys(payload).length) {
      notifyAgentKnownFields(payload);
    }
  }, [
    isMenuMode,
    isClaimForm,
    activeVoiceFormId,
    dynamicForm.formLoading,
    dynamicForm.formSchema,
    dynamicForm.fieldStatuses,
    policiesLoading,
    dynamicForm.buildClientFieldsPayload,
    notifyAgentKnownFields,
  ]);

  useEffect(() => {
    if (!isMenuMode) return;
    if (liveSocketRef.current?.readyState !== WebSocket.OPEN) return;
    notifyAgentKnownFields(buildMenuAgentFields());
  }, [
    isMenuMode,
    policiesLoading,
    customerPolicies,
    buildMenuAgentFields,
    notifyAgentKnownFields,
  ]);

  useEffect(() => {
    customerPoliciesRef.current = customerPolicies;
  }, [customerPolicies]);

  /** Auto-fill policy number from account whenever policy type is known. */
  useEffect(() => {
    syncPolicyNumberFromAccount();
  }, [
    voicePolicyType,
    customerPolicies,
    policiesLoading,
    syncPolicyNumberFromAccount,
    state.fields.policyType?.status,
  ]);

  useEffect(() => {
    if (!isClaimForm) return;
    if (liveSocketRef.current?.readyState !== WebSocket.OPEN) return;
    notifyAgentKnownFields();
  }, [
    isClaimForm,
    state.fields.policyNumber?.status,
    state.fields.policyNumber?.value,
    state.fields.policyType?.status,
    state.fields.policyType?.value,
    customerPolicies,
    policiesLoading,
    notifyAgentKnownFields,
  ]);

  useEffect(() => {
    if (!isClaimForm) return;
    if (policiesLoading || !customerPolicies.length) return;
    if (liveSocketRef.current?.readyState === WebSocket.OPEN) {
      notifyAgentKnownFields();
    }
  }, [
    isClaimForm,
    policiesLoading,
    customerPolicies,
    notifyAgentKnownFields,
  ]);

  useEffect(() => {
    if (!isClaimForm || policiesLoading) return;
    const policyType = state.fields.policyType?.value?.trim();
    const policyNo = state.fields.policyNumber;
    if (!policyType || policyType.startsWith("Missing")) return;
    if (policyNo?.status === "complete" && policyNo.value && !/^missing:/i.test(policyNo.value)) {
      return;
    }
    syncPolicyNumberFromAccount();
  }, [
    isClaimForm,
    policiesLoading,
    customerPolicies,
    state.fields.policyType?.value,
    state.fields.policyNumber?.status,
    syncPolicyNumberFromAccount,
  ]);

  const applyServerState = useCallback(
    (nextState: IntakeState) => {
      setState((prev) => {
        const normalized = normalizeServerState(nextState);
        const withTranscript = {
          ...normalized,
          transcript: mergeLiveTranscript(normalized.transcript, prev.transcript),
        };
        if (activeVoiceFormIdRef.current !== "file_claim") {
          return { ...withTranscript, fields: prev.fields };
        }
        if (!transcriptHasClaimantAnswer(withTranscript.transcript)) {
          return { ...withTranscript, fields: prev.fields };
        }
        let hinted = applyHintsFromFullTranscript(withTranscript);
        const claimantLines = hinted.transcript
          .filter((t) => t.speaker === "Claimant" && t.text?.trim())
          .map((t) => t.text!.trim());
        hinted = {
          ...hinted,
          fields: mergeClaimFields(prev.fields, hinted.fields, claimantLines),
        };
        const policyType = hinted.fields.policyType?.value?.trim();
        if (
          policyType &&
          hinted.fields.policyType?.status !== "missing" &&
          customerPoliciesRef.current.length > 0
        ) {
          const claimantText = hinted.transcript
            .filter((t) => t.speaker === "Claimant" && t.text?.trim())
            .map((t) => t.text)
            .join(" ");
          hinted = applyPolicyLookup(hinted, policyType, claimantText);
        }
        return hinted;
      });
    },
    [applyPolicyLookup]
  );

  const api = useCallback(
    async (path: string, options: RequestInit = {}) => {
      const response = await fetch(`${apiOrigin}${path}`, {
        headers: { "Content-Type": "application/json" },
        ...options,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          (payload as { detail?: string }).detail ||
            `Request failed with status ${response.status}`
        );
      }
      return payload as {
        session_id?: string;
        state?: IntakeState;
        model?: string;
        has_api_key?: boolean;
      };
    },
    [apiOrigin]
  );

  const stopLiveVoice = useCallback((sendClose = true) => {
    stopAgentPlayback();
    setIsRecording(false);
    isRecordingRef.current = false;
    closeAfterAgentTurnRef.current = false;
    inputProcessorRef.current?.disconnect();
    inputSourceRef.current?.disconnect();
    audioStreamRef.current?.getTracks().forEach((track) => track.stop());
    inputProcessorRef.current = null;
    inputSourceRef.current = null;
    audioStreamRef.current = null;
    if (sendClose) {
      try {
        liveSocketRef.current?.send(JSON.stringify({ type: "close" }));
      } catch {
        /* ignore */
      }
    }
    liveSocketRef.current?.close();
    liveSocketRef.current = null;
    if (sendClose) setCallStatus("Live voice stopped");
  }, [stopAgentPlayback]);

  const playPcm24 = useCallback((base64: string) => {
    const ctx = audioContextRef.current || new AudioContext();
    audioContextRef.current = ctx;
    agentSpeakingRef.current = true;
    const pcm = base64ToInt16Array(base64);
    const audioBuffer = ctx.createBuffer(1, pcm.length, 24000);
    const channel = audioBuffer.getChannelData(0);
    for (let i = 0; i < pcm.length; i += 1) {
      channel[i] = pcm[i] / 32768;
    }
    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(ctx.destination);
    const startAt = Math.max(ctx.currentTime, nextPlaybackTimeRef.current);
    source.onended = () => {
      activePlaybackSourcesRef.current = activePlaybackSourcesRef.current.filter(
        (s) => s !== source
      );
      if (activePlaybackSourcesRef.current.length === 0) {
        window.setTimeout(() => {
          if (activePlaybackSourcesRef.current.length === 0) {
            agentSpeakingRef.current = false;
          }
        }, 120);
      }
    };
    activePlaybackSourcesRef.current.push(source);
    source.start(startAt);
    nextPlaybackTimeRef.current = startAt + audioBuffer.duration;
  }, []);

  const connectLiveVoice = useCallback(async () => {
    if (liveSocketRef.current?.readyState === WebSocket.OPEN) return;
    const socket = new WebSocket(`${wsOrigin}/ws/live`);
    // Legacy Python: new WebSocket(`${(process.env.REACT_APP_CHAT_GEMINI_LIVE_URL || "http://127.0.0.1:8002").replace(/^http/, "ws")}/ws/live`);
    liveSocketRef.current = socket;

    socket.onopen = () => {
      if (!mountedRef.current) return;
      setCallStatus("Live voice streaming");
      lastClientFieldsKeyRef.current = "";
      setState((prev) => {
        const hasAgentLine = prev.transcript.some(
          (t) => t.speaker === "Agent" && t.text?.trim()
        );
        if (hasAgentLine) return prev;
        return {
          ...prev,
          transcript: [
            ...prev.transcript,
            {
              speaker: "Agent",
              text: VOICE_MENU_GREETING,
              time: nowTime(),
              streaming: true,
            },
          ],
        };
      });
      notifyAgentKnownFields(buildMenuAgentFields());
    };

    socket.onmessage = (event) => {
      const message = JSON.parse(event.data as string) as {
        type: string;
        session_id?: string;
        model?: string;
        speaker?: string;
        text?: string;
        final?: boolean;
        data?: string;
        state?: IntakeState;
        message?: string;
      };

      if (message.type === "session") {
        sessionIdRef.current = message.session_id || null;
      } else if (message.type === "transcript" && message.speaker && message.text) {
        if (
          message.speaker === "Agent" &&
          isInternalTranscriptLeak(message.text)
        ) {
          return;
        }
        let detectedIntent: ReturnType<
          typeof detectVoiceFormIntentFromTranscript
        > = null;
        setState((prev) => {
          let base = prev;
          if (message.speaker === "Agent" && message.final) {
            base = {
              ...prev,
              transcript: prev.transcript.filter(
                (t) => !(t.speaker === "Agent" && t.streaming)
              ),
            };
          }
          const next =
            upsertStreamingTurn(
              base,
              message.speaker!,
              message.text!,
              message.final
            ) || base;

          // Streaming deltas: update transcript only. Heavy claim extraction/policy
          // lookup on every chunk blocks the JS thread and delays agent audio playback.
          if (!message.final) {
            return next;
          }

          const isClaimIntake = activeVoiceFormIdRef.current === "file_claim";
          if (!isClaimIntake) {
            if (message.speaker === "Claimant") {
              detectedIntent = detectVoiceFormIntentFromTranscript(
                next.transcript,
                activeVoiceFormIdRef.current
              );
            }
            if (
              message.speaker === "Agent" &&
              closeAfterAgentTurnRef.current &&
              agentClosedConversation(message.text!)
            ) {
              window.setTimeout(() => stopLiveVoice(), 800);
            }
            return next;
          }

          let withHints =
            message.speaker === "Claimant"
              ? applyHintsFromFullTranscript(next)
              : next;
          if (message.speaker === "Claimant") {
            const fieldsFromTranscript = { ...withHints.fields };
            for (const id of ["policyType", "policyNumber"] as const) {
              const prevField = prev.fields[id];
              if (
                prevField &&
                prevField.source === "customer policies" &&
                prevField.status === "complete" &&
                prevField.value &&
                !/^missing:/i.test(prevField.value)
              ) {
                fieldsFromTranscript[id] = prevField;
              }
            }
            withHints = { ...withHints, fields: fieldsFromTranscript };
            const policyTypeField = withHints.fields.policyType;
            const claimantFullText = next.transcript
              .filter((t) => t.speaker === "Claimant" && t.text?.trim())
              .map((t) => t.text)
              .join(" ");
            const policyTypeValue = policyTypeField?.value?.trim();
            if (
              policyTypeValue &&
              policyTypeField?.status !== "missing" &&
              !/^missing:/i.test(policyTypeValue)
            ) {
              withHints = applyPolicyLookup(
                withHints,
                policyTypeValue,
                claimantFullText
              );
            }
            const spoken = message.text || claimantFullText;
            if (spoken) {
              withHints = applyPolicyNumberFromSpeech(withHints, spoken);
            }
            if (claimantAskedToClose(message.text!)) {
              closeAfterAgentTurnRef.current = true;
            }
            detectedIntent = detectVoiceFormIntentFromTranscript(
              withHints.transcript,
              activeVoiceFormIdRef.current
            );
          }
          if (
            message.speaker === "Agent" &&
            closeAfterAgentTurnRef.current &&
            agentClosedConversation(message.text!)
          ) {
            window.setTimeout(() => stopLiveVoice(), 800);
          }
          return withHints;
        });
        if (detectedIntent) {
          applyDetectedFormIntent(detectedIntent);
        }
      } else if (message.type === "audio" && message.data) {
        playPcm24(message.data);
      } else if (message.type === "state" && message.state) {
        // Defer form merge so agent PCM playback is not blocked on the WS handler.
        const serverState = message.state;
        window.setTimeout(() => applyServerState(serverState), 0);
      } else if (message.type === "interrupted") {
        stopAgentPlayback();
      } else if (message.type === "status" && message.message) {
        setCallStatus(String(message.message));
      } else if (message.type === "error" && message.message) {
        const errText = String(message.message);
        appendLocalError(errText);
        setCallStatus(errText);
        stopLiveVoice(false);
      }
    };

    socket.onclose = () => {
      if (isRecordingRef.current) setCallStatus("Live voice disconnected");
    };

    socket.onerror = () => {
      appendLocalError(
        "Live voice WebSocket failed. Check the backend server."
      );
    };

    await new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(
        () => reject(new Error("Live voice connection timed out.")),
        8000
      );
      socket.addEventListener(
        "open",
        () => {
          window.clearTimeout(timeout);
          resolve();
        },
        { once: true }
      );
      socket.addEventListener(
        "error",
        () => {
          window.clearTimeout(timeout);
          reject(new Error("Live voice connection failed."));
        },
        { once: true }
      );
    });
  }, [
    appendLocalError,
    applyDetectedFormIntent,
    applyPolicyLookup,
    applyPolicyNumberFromSpeech,
    applyServerState,
    buildMenuAgentFields,
    notifyAgentKnownFields,
    playPcm24,
    stopAgentPlayback,
    stopLiveVoice,
    wsOrigin,
  ]);

  const startLiveVoice = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.AudioContext) {
      appendLocalError(
        "This browser cannot capture microphone audio. Type the claimant turn instead."
      );
      return;
    }
    try {
      await connectLiveVoice();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      const ctx = audioContextRef.current || new AudioContext();
      audioContextRef.current = ctx;
      await ctx.resume();
      const source = ctx.createMediaStreamSource(stream);
      inputSourceRef.current = source;
      const processor = ctx.createScriptProcessor(2048, 1, 1);
      inputProcessorRef.current = processor;
      const SPEECH_RMS = 0.012;
      const SILENCE_END_MS = 200;
      processor.onaudioprocess = (event) => {
        event.outputBuffer.getChannelData(0).fill(0);
        if (liveSocketRef.current?.readyState !== WebSocket.OPEN) return;
        // Half-duplex: do not stream mic while the agent is talking. Echo/noise
        // was keeping Gemini VAD open and delaying replies after "auto"/"home".
        if (agentSpeakingRef.current) return;

        const input = event.inputBuffer.getChannelData(0);
        let sum = 0;
        for (let i = 0; i < input.length; i += 1) {
          sum += input[i] * input[i];
        }
        const level = Math.sqrt(sum / Math.max(1, input.length));
        const frameMs = (input.length / ctx.sampleRate) * 1000;

        if (level >= SPEECH_RMS) {
          userSpeechActiveRef.current = true;
          userSilenceMsRef.current = 0;
          speechEndSentRef.current = false;
          const pcm16 = resampleToPcm16(input, ctx.sampleRate, 16000);
          liveSocketRef.current.send(
            JSON.stringify({
              type: "audio",
              data: arrayBufferToBase64(pcm16),
            })
          );
          return;
        }

        if (!userSpeechActiveRef.current) {
          return;
        }

        userSilenceMsRef.current += frameMs;
        if (userSilenceMsRef.current < SILENCE_END_MS) {
          const pcm16 = resampleToPcm16(input, ctx.sampleRate, 16000);
          liveSocketRef.current.send(
            JSON.stringify({
              type: "audio",
              data: arrayBufferToBase64(pcm16),
            })
          );
          return;
        }

        if (!speechEndSentRef.current) {
          speechEndSentRef.current = true;
          userSpeechActiveRef.current = false;
          userSilenceMsRef.current = 0;
          liveSocketRef.current.send(JSON.stringify({ type: "speech_end" }));
        }
      };
      source.connect(processor);
      processor.connect(ctx.destination);
      setIsRecording(true);
      isRecordingRef.current = true;
      setCallStatus("Live voice streaming");
    } catch (error) {
      const err = error as Error & { name?: string };
      const denied =
        err.name === "NotAllowedError" ||
        /denied|permission/i.test(err.message || "");
      appendLocalError(
        denied
          ? `Microphone access was denied. Allow microphone access for ${apiOrigin}, or use the text box.`
          : `Live voice failed: ${err.message}`
      );
      stopLiveVoice(false);
    }
  }, [apiOrigin, appendLocalError, connectLiveVoice, notifyAgentKnownFields, stopLiveVoice]);

  const createSession = useCallback(async () => {
    stopLiveVoice(true);
    setCallStatus("Connecting to backend");
    setTextDisabled(true);
    selectedPolicyIdRef.current = null;
    pendingPolicyCandidatesRef.current = [];
    lastClientFieldsKeyRef.current = "";
    setActiveVoiceFormId(null);
    lastAgentPromptHintRef.current = "";
    setState(buildEmptyState(apiOrigin));
    try {
      const payload = await api("/api/sessions", { method: "POST" });
      sessionIdRef.current = payload.session_id || null;
      if (payload.state) {
        const normalized = normalizeServerState(payload.state);
        setState({
          ...normalized,
          fields: buildEmptyState(apiOrigin).fields,
        });
      }
      setCallStatus(
        payload.has_api_key ? "Active - API connected" : "API key required"
      );
      setTextDisabled(false);
    } catch (error) {
      setCallStatus("Backend unavailable");
      appendLocalError((error as Error).message);
    }
  }, [api, apiOrigin, appendLocalError, stopLiveVoice]);

  const sendClaimantTurn = useCallback(
    async (text: string) => {
      if (liveSocketRef.current?.readyState === WebSocket.OPEN) {
        liveSocketRef.current.send(JSON.stringify({ type: "text", text }));
        setState((prev) => {
          const next = upsertStreamingTurn(prev, "Claimant", text, true) || prev;
          const hit = detectVoiceFormIntentFromTranscript(
            next.transcript,
            activeVoiceFormIdRef.current
          );
          if (hit) {
            window.queueMicrotask(() => applyDetectedFormIntent(hit));
          }
          return next;
        });
        return;
      }
      if (!sessionIdRef.current) {
        appendLocalError("No backend session is active. Start a new intake first.");
        return;
      }
      setState((prev) => {
        const next = upsertStreamingTurn(prev, "Claimant", text, true) || prev;
        const hit = detectVoiceFormIntentFromTranscript(
          next.transcript,
          activeVoiceFormIdRef.current
        );
        if (hit) {
          window.queueMicrotask(() => applyDetectedFormIntent(hit));
        }
        return next;
      });
      setCallStatus("Processing with Gemini");
      setTextDisabled(true);
      try {
        const payload = await api("/api/message", {
          method: "POST",
          body: JSON.stringify({ session_id: sessionIdRef.current, text }),
        });
        if (payload.state) setState(normalizeServerState(payload.state));
        setCallStatus("Active - API connected");
      } catch (error) {
        setCallStatus("API error");
        appendLocalError((error as Error).message);
      } finally {
        setTextDisabled(false);
      }
    },
    [api, appendLocalError, applyDetectedFormIntent]
  );

  useEffect(() => {
    mountedRef.current = true;
    createSession();
    return () => {
      mountedRef.current = false;
      stopLiveVoice(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTextSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = textInput.trim();
    if (!value) return;
    setTextInput("");
    sendClaimantTurn(value);
  };

  return (
    <div className="voice-live-demo">
      <div className="app-shell">
        <main className="workspace">
          <header className="topbar">
            <div className="title-group">
              <h1>Insurance Claim Live Agent Team</h1>
            </div>
          </header>

          <section className="dashboard" aria-label="Live intake cockpit">
            <section className="panel call-panel">
              <div className="panel-header">
                <div>
                  <p className="panel-kicker">Live call</p>
                  <h2>Voice and transcript</h2>
                </div>
                <span className="status-pill active">{callStatus}</span>
              </div>

              <div className="voice-stage">
                <div className="wave left" aria-hidden="true">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <span key={`l-${i}`} />
                  ))}
                </div>
                <button
                  type="button"
                  className={`mic-button${isRecording ? " recording" : ""}`}
                  aria-label={
                    isRecording ? "Stop live voice" : "Start live voice"
                  }
                  onClick={() => {
                    if (isRecording) stopLiveVoice();
                    else startLiveVoice();
                  }}
                >
                  <span className="mic-icon" aria-hidden="true" />
                </button>
                <div className="wave right" aria-hidden="true">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <span key={`r-${i}`} />
                  ))}
                </div>
              </div>

              <div className="call-actions">
                <button
                  type="button"
                  className="primary-action"
                  onClick={createSession}
                >
                  New live intake
                </button>
                <button
                  type="button"
                  className="secondary-action"
                  onClick={createSession}
                >
                  Reset
                </button>
              </div>

              <div className="transcript-shell">
              <div className="transcript-header">
                <h3>Live transcript</h3>
                <span>Streaming</span>
              </div>
              <div className="transcript" aria-live="polite">
                {!state.transcript.some(
                  (t) => t.speaker === "Claimant" && t.text?.trim()
                ) ? (
                  <p className="transcript-empty-hint">
                    Your words will appear here when you speak into the microphone
                    or use the text box below.
                  </p>
                ) : null}
                {state.transcript
                  .filter(
                    (turn) =>
                      turn.speaker !== "Agent" ||
                      !isInternalTranscriptLeak(turn.text || "")
                  )
                  .map((turn: TranscriptTurn, index) => {
                    const isYou = turn.speaker === "Claimant";
                    return (
                  <article
                    key={`${turn.speaker}-${index}-${turn.time || ""}`}
                    className={`turn ${isYou ? "claimant" : "agent"}${turn.streaming ? " streaming" : ""}`}
                  >
                    <div className="speaker-icon">
                      {isYou ? "You" : "AI"}
                    </div>
                    <div className="bubble">
                      <strong>{isYou ? "You" : "Agent"}</strong>
                      <time>{turn.time || nowTime()}</time>
                      <p>{turn.text}</p>
                      {turn.streaming ? (
                        <span className="streaming-label">Speaking…</span>
                      ) : null}
                    </div>
                  </article>
                    );
                  })}
              </div>
              </div>

              <form className="text-entry" onSubmit={handleTextSubmit}>
                <input
                  type="text"
                  autoComplete="off"
                  placeholder="Type a claimant response..."
                  aria-label="Type a claimant response"
                  value={textInput}
                  disabled={textDisabled}
                  onChange={(e) => setTextInput(e.target.value)}
                />
                <button type="submit" aria-label="Send response" disabled={textDisabled}>
                  Send
                </button>
              </form>
            </section>

            <section className="panel claim-panel">
              {voiceSentiment.isActive ? (
                <div className="claim-panel-sentiment-row">
                  <SentimentIndicator
                    presentation={voiceSentiment.presentation}
                    loading={voiceSentiment.loading}
                    description={voiceSentiment.description}
                  />
                </div>
              ) : null}

              <div className="claim-panel-section claim-panel-section--enquires">
                <div className="claim-section-heading">
                  <h2>Enquires</h2>
                  <span className="status-line">
                    {csrAssist.enquiries.length
                      ? `${csrAssist.enquiries.length} question(s)`
                      : csrAssist.loading
                        ? "Prefetching answers"
                        : "Listening for questions"}
                  </span>
                </div>
                <div className="claim-section-body">
                  <CsrAssistEnquiresPanel
                    enquiries={csrAssist.enquiries}
                    loading={csrAssist.loading}
                    error={csrAssist.error}
                    hasConversation={csrAssist.hasConversation}
                  />
                </div>
              </div>

              <div className="claim-panel-section claim-panel-section--form">
                <div className="claim-section-heading">
                  <h2>{formSectionTitle}</h2>
                  <span className="status-line">{formSectionStatus}</span>
                </div>
                <div className="claim-section-body">
                {isMenuMode ? (
                  <p className="claim-section-placeholder">
                    Use the microphone or type below. Say what you need — for example:
                    file a claim, add a driver, or add a vehicle. The form will appear here
                    once you choose.
                  </p>
                ) : !isClaimForm ? (
                  <VoiceDynamicFormPanel
                    policy={dynamicForm.policy}
                    fieldStatuses={dynamicForm.fieldStatuses}
                    formError={dynamicForm.formError}
                  />
                ) : (
                INITIAL_FIELD_GROUPS.map((group) => {
                  const pairedIds = new Set([
                    "policyType",
                    "policyNumber",
                    "locationType",
                    "incidentType",
                    "date",
                    "time",
                  ]);
                  const largeIds = new Set([
                    "incidentLocation",
                    "incidentDescription",
                  ]);
                  const pairedFields = group.fields.filter(([id]) =>
                    pairedIds.has(id)
                  );
                  const largeFields = group.fields.filter(([id]) =>
                    largeIds.has(id)
                  );

                  const renderField = (
                    id: string,
                    label: string,
                    size: "default" | "large"
                  ) => {
                    const field = state.fields[id] || {
                      label,
                      value: `Missing: ${label.toLowerCase()}`,
                      status: "missing",
                      source: "-",
                    };
                    return (
                      <div
                        key={id}
                        className={`field-cell field-cell--${size}`}
                      >
                        <div className="field-label">{field.label}</div>
                        <div className={`field-value ${field.status}`}>
                          {field.value}
                        </div>
                      </div>
                    );
                  };

                  return (
                    <section key={group.title} className="field-group">
                      <div className="group-title group-title--subsection">
                        <h3>{group.title}</h3>
                      </div>
                      <div className="claim-fields-layout">
                        <div className="field-pairs">
                          {Array.from(
                            { length: Math.ceil(pairedFields.length / 2) },
                            (_, rowIndex) => (
                              <div
                                key={rowIndex}
                                className="field-pair-row"
                              >
                                {pairedFields
                                  .slice(rowIndex * 2, rowIndex * 2 + 2)
                                  .map(([id, label]) =>
                                    renderField(id, label, "default")
                                  )}
                              </div>
                            )
                          )}
                        </div>
                        <div className="field-stack-large">
                          {largeFields.map(([id, label]) =>
                            renderField(id, label, "large")
                          )}
                        </div>
                      </div>
                    </section>
                  );
                })
                )}
              </div>
              </div>
            </section>
          </section>
        </main>
      </div>
    </div>
  );
};

export default VoiceLiveDemo;
